import { useMemo } from 'react'

import {
  NotificationsAvatarUsersDocument,
  NotificationsAvatarUsersQuery,
  NotificationsRequestsDocument,
  NotificationsRequestsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

export type AvatarUser = NotificationsAvatarUsersQuery['employees']['employees'][number]

export const useNotificationsRequests = () => {
  const { client, data, loading } = useQuery(NotificationsRequestsDocument, {
    variables: {
      companyId: useCurrentSession().company.id,
    },
  })

  const requests = data?.notifications?.requests ?? []

  // Extract unique avatar access IDs from requests
  const accessIds = useMemo(() => {
    const ids = requests
      .map((request) => request.avatarAccessIdentifier)
      .filter((id): id is number => id != null)
      .map(String)
    return [...new Set(ids)]
  }, [requests])

  // Fetch avatar users by access IDs
  const { data: avatarUsersData, loading: avatarUsersLoading } = useQuery(
    NotificationsAvatarUsersDocument,
    {
      variables: { accessIds },
      skip: accessIds.length === 0,
    }
  )

  // Create a map of access ID to avatar user for efficient lookup
  const avatarUsersMap = useMemo(() => {
    const map = new Map<string, AvatarUser>()
    const nodes = avatarUsersData?.employees?.employees ?? []
    for (const node of nodes) {
      if (node.access?.id) {
        map.set(node.access.id, node)
      }
    }
    return map
  }, [avatarUsersData])

  // Remove the request with the passed 'id' from the list if
  // the passed 'id' is found
  const removeRequestById = (id: string) => {
    client.cache.evict({
      id: client.cache.identify({ id: id, __typename: 'NotificationsRequest' }),
    })
  }

  return {
    requests,
    loading: loading || avatarUsersLoading,
    removeRequestById,
    avatarUsersMap,
  }
}

export type InboxRequest = NotificationsRequestsQuery['notifications']['requests'][number]
